import {
  Button,
  Heading,
  Input,
  SimpleGrid,
  Text,
  VStack,
  Spinner,
  Center,
  Box,
  Icon,
  Card as ChakraCard,
  CardBody,
} from '@chakra-ui/react'
import debounce from 'lodash.debounce'
import { FunctionComponent, useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import type { RootState } from '../../app/store'
import Card from '../../common/components/Card'
import { patientsApi, useLazyGetPatientsQuery } from '../../services/patients'
import NewPatientModal from '../PatientsPage/NewPatientModal'
import { selectPatient } from '../../features/patient/PatientContextSlice'
import { setDeliveryState } from './ScriptForm/PrescribedItem/PrescribedItemSlice'
import { Patient, Prescriber } from '../../types'
import { useLazyGetPatientByExternalIdQuery } from '../../services/patientsBFF'
import { LimitedPrescriberProfile } from '../../features/users/UsersSlice'
import { PrescriptionDeliveryMethod } from '../../types/prescription-delivery-method'
import {
  useGetAvailableIntegrationsQuery,
  useConnectIntegrationMutation,
} from '../../features/integration/integrationSlice'
import { useSSOContext } from '../../features/sso'
import { FiAlertCircle, FiRefreshCw } from 'react-icons/fi'
import { format, parseISO } from 'date-fns'
import { resetAllValidationResults } from '../PatientsPage/PatientFormSlice'
import { useHasEntitlement } from '../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types/organization-entitlements'
import { SubscriptionNotificationModal } from '../Billing/SubscriptionNotificationModal'
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard'
import { SelectedPatientIdentity } from './SelectedPatientIdentity'
import { analyticsAction, trackEvent } from '../../common/utils/analytics'

const LoadingState: FunctionComponent<Record<string, never>> = () => (
  <Center py={8}>
    <VStack spacing={6}>
      <Box
        bg="blue.50"
        border="1px solid"
        borderColor="blue.200"
        borderRadius="lg"
        p={6}
        textAlign="center"
        maxW="400px"
        w="100%"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="blue.700" fontWeight="medium" fontSize="md">
            Patient data being acquired...
          </Text>
        </VStack>
      </Box>
    </VStack>
  </Center>
)

const ErrorState: FunctionComponent<{
  error: boolean
  onSelectAnother: () => void
  canManagePatient: boolean
  errorMessage?: string
}> = ({ error, onSelectAnother, canManagePatient, errorMessage }) => {
  return error ? (
    <VStack align="center" py={4}>
      <Box
        bg="red.50"
        border="1px solid"
        borderColor="red.200"
        borderRadius="lg"
        p={6}
        textAlign="center"
        paddingY="8px"
        maxW="800px"
        w="90%"
      >
        <VStack spacing={4}>
          <Icon as={FiAlertCircle} w={8} h={8} color="red.500" />
          <Text color="red.700" fontWeight="medium" fontSize="md">
            {errorMessage || 'Failed to fetch patient data. Please try again.'}
          </Text>
          {canManagePatient && (
            <Button
              leftIcon={<Icon as={FiRefreshCw} />}
              colorScheme="blue"
              variant="outline"
              onClick={onSelectAnother}
              _hover={{
                bg: 'blue.50',
                borderColor: 'blue.300',
              }}
              size="md"
            >
              Select another patient
            </Button>
          )}
        </VStack>
      </Box>
    </VStack>
  ) : null
}

const PatientSearchResultItem: FunctionComponent<{ title: string; value: string }> = ({
  title,
  value,
}) => {
  return (
    <Box mb={{ base: 2, md: 0 }} _last={{ mb: 0 }}>
      <Text fontSize="sm">{title}</Text>
      <Text fontWeight="bold">{value}</Text>
    </Box>
  )
}

const PatientSearch: FunctionComponent<{
  searchQuery: string
  onSearchChange: (value: string) => void
  onSearch: () => void
  onSelectPatient: (patient: any) => void
  isDeactivated: boolean
  prescriber: Prescriber
  onAddPatient: (patient: any) => void
  results: any[]
  isSearching: boolean
  allowRegistration?: boolean
}> = ({
  searchQuery,
  onSearchChange,
  onSearch,
  onSelectPatient,
  isDeactivated,
  prescriber,
  onAddPatient,
  results,
  isSearching,
  allowRegistration = true,
}) => {
  const dispatch = useAppDispatch()
  const {
    isOrgRegistrationAndPaymentFeatureOn,
    isSubscriptionNotificationOpen,
    closeSubscriptionNotification,
    runWithValidSubscription,
  } = useSubscriptionGuard()

  return (
    <VStack alignItems="stretch">
      <Text fontWeight="bold" flexGrow="1">
        Select a patient
      </Text>
      <Input
        maxWidth="100%"
        bgColor="white"
        placeholder="Search patient ( at least 2 characters )"
        aria-label="Search patient"
        disabled={isDeactivated}
        {...analyticsAction('new_script_patient_search')}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSearch()
          }
        }}
      />

      {searchQuery && (
        <VStack>
          {isSearching ? (
            <Spinner size="sm" />
          ) : results?.length > 0 ? (
            results.map((patient) => (
              <ChakraCard
                variant={'elevated'}
                key={patient.id}
                width="100%"
                cursor="pointer"
                _hover={{ backgroundColor: 'gray.100' }}
                onClick={() => onSelectPatient(patient)}
                {...analyticsAction('new_script_patient_select')}
              >
                <CardBody>
                  <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4} width="100%">
                    <PatientSearchResultItem
                      title="Name"
                      value={`${patient.given_names} ${patient.family_name}`}
                    />
                    <PatientSearchResultItem
                      title="Date of Birth"
                      value={format(parseISO(patient.date_of_birth), 'd MMMM yyyy')}
                    />
                    <PatientSearchResultItem
                      title="Address"
                      value={`${patient.address_1}, ${patient.state} ${patient.postcode}`}
                    />
                  </SimpleGrid>
                </CardBody>
              </ChakraCard>
            ))
          ) : (
            <Text>No patients found</Text>
          )}
        </VStack>
      )}
      {allowRegistration && (
        <NewPatientModal
          onAdd={onAddPatient}
          hidePrescriberSelection={true}
          selectedPrescriber={prescriber as LimitedPrescriberProfile}
        >
          {({ openModal }) => (
            <Button
              colorScheme="blue"
              onClick={() =>
                runWithValidSubscription(() => {
                  dispatch(resetAllValidationResults())
                  openModal()
                })
              }
              disabled={isDeactivated}
              {...analyticsAction('new_script_new_patient')}
            >
              New Patient
            </Button>
          )}
        </NewPatientModal>
      )}
      {isOrgRegistrationAndPaymentFeatureOn && (
        <SubscriptionNotificationModal
          isOpen={isSubscriptionNotificationOpen}
          onClose={closeSubscriptionNotification}
        />
      )}
    </VStack>
  )
}

export interface PatientLoadState {
  isLoading: boolean
  showError: boolean
}

export const PatientWorkflow: FunctionComponent<{
  patientLoadState?: PatientLoadState
  onPatientChosen?: (patient: Patient) => void
  allowRegistration?: boolean
  enableExternalIntegration?: boolean
  forcePatientSearch?: boolean
}> = ({
  patientLoadState,
  onPatientChosen,
  allowRegistration = true,
  enableExternalIntegration = true,
  forcePatientSearch = false,
}) => {
  const hasAttemptedFetch = useRef(false)
  const hasAttemptedIntegrationConnection = useRef(false)
  const [patientSearchQuery, setPatientSearchQuery] = useState('')
  const [showFetchPatientError, setShowFetchPatientError] = useState(false)
  const [isIntegrationConnected, setIsIntegrationConnected] = useState(false)

  const { data: availableIntegrations } = useGetAvailableIntegrationsQuery(undefined, {
    skip: !enableExternalIntegration,
  })
  const [connectIntegration] = useConnectIntegrationMutation()
  const isSharedPatientsFeatureOn = useHasEntitlement(FeatureKey.SHARED_PATIENTS)

  const [searchPatients, searchPatientsResult] = useLazyGetPatientsQuery()

  // Once per search request, by result count only: the term stays out of analytics.
  const trackedSearchRequestId = useRef<string | undefined>(undefined)
  useEffect(() => {
    const { requestId, isFetching, currentData } = searchPatientsResult
    if (!requestId || isFetching || !currentData) return
    if (trackedSearchRequestId.current === requestId) return
    trackedSearchRequestId.current = requestId
    trackEvent('patient_searched', {
      source: 'new_script',
      resultCount: currentData.meta?.totalItems ?? currentData.patients.length,
    })
  }, [searchPatientsResult])

  const prescriber = useAppSelector(({ prescriber }: RootState) => prescriber?.prescriber)
  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isAbleToManagePatient = organizationSettings?.enableManagePatient
  const isDeactivatedPrescriber = !prescriber?.active
  const defaultPrescriptionDeliveryMethod = organizationSettings?.defaultPrescriptionDeliveryMethod
  const selectedPatient = useSelector(
    ({ patientDetail }: RootState) => patientDetail.selectedPatient
  )

  const dispatch = useAppDispatch()
  const {
    context: clientSSOContextData,
    externalPatientFetchId,
    metadata,
    isManualIntegrationClient,
    providerId,
    clearExternalPatientData,
  } = useSSOContext()

  const [syncPatient, syncPatientResult] = useLazyGetPatientByExternalIdQuery()

  const isAbleToAutoBuildIntegrations =
    enableExternalIntegration &&
    organizationSettings?.enableAutoBuildIntegration &&
    !isDeactivatedPrescriber &&
    prescriber?.entityId &&
    providerId

  const getErrorMessage = (error: any): string => {
    if (!error) return 'Failed to fetch patient data. Please try again.'

    const errorData = error.data || error
    const message = errorData?.message
    const statusCode = errorData?.statusCode

    if (message === 'Patient IHI is not found' && statusCode === 409) {
      return 'Patient IHI validation failed. Please verify the patient information and try again.'
    }

    if (message === 'Patient not found' && statusCode === 404) {
      return 'Patient not found in the system. Please check the patient details and try again.'
    }

    return 'Failed to fetch patient data. Please try again.'
  }

  useEffect(() => {
    const checkAndConnectIntegration = async () => {
      if (!availableIntegrations || !isAbleToAutoBuildIntegrations) {
        return
      }
      const errorKey = `integration_failed_${providerId}`
      const hasFailedBefore = localStorage.getItem(errorKey)
      if (hasFailedBefore) {
        hasAttemptedIntegrationConnection.current = true
        return
      }
      if (hasAttemptedIntegrationConnection.current) {
        return
      }

      const isProviderConnected = availableIntegrations.some(
        (integration) => integration.id === providerId && integration.connected
      )

      if (isProviderConnected) {
        setIsIntegrationConnected(true)
        hasAttemptedIntegrationConnection.current = true
        return
      }
      hasAttemptedIntegrationConnection.current = true

      try {
        await connectIntegration(providerId).unwrap()
        setIsIntegrationConnected(true)
      } catch (error: any) {
        console.error('Failed to connect integration:', error)
        localStorage.setItem(errorKey, 'true')
        setShowFetchPatientError(false)
      }
    }

    checkAndConnectIntegration()
  }, [providerId, availableIntegrations, isAbleToAutoBuildIntegrations])

  useEffect(() => {
    if (
      externalPatientFetchId &&
      enableExternalIntegration &&
      !selectedPatient.id &&
      !hasAttemptedFetch.current &&
      (isIntegrationConnected || isManualIntegrationClient)
    ) {
      hasAttemptedFetch.current = true
      syncPatient({
        externalPatientFetchId,
        ...(providerId && { providerId }),
        metadata,
      })
    }
  }, [
    externalPatientFetchId,
    enableExternalIntegration,
    syncPatient,
    providerId,
    selectedPatient.id,
    isIntegrationConnected,
    isManualIntegrationClient,
  ])

  useEffect(() => {
    if (syncPatientResult.error) {
      setShowFetchPatientError(true)
    }
  }, [syncPatientResult.error])

  useEffect(() => {
    if (syncPatientResult.data) {
      dispatch(selectPatient(syncPatientResult.data))
    }
  }, [syncPatientResult.data, dispatch])

  useEffect(() => {
    dispatch(
      setDeliveryState({
        sendViaEmail: defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.Email,
        sendViaSMS: defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.SMS,
        printPaperToken:
          defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.Paper_Token,
        recipientEmail: selectedPatient?.email,
        recipientMobile: selectedPatient?.phone_number,
      })
    )
  }, [dispatch, selectedPatient, defaultPrescriptionDeliveryMethod])

  const searchPatientsRef = useRef(searchPatients)
  searchPatientsRef.current = searchPatients

  const isSharedPatientsFeatureOnRef = useRef(isSharedPatientsFeatureOn)
  isSharedPatientsFeatureOnRef.current = isSharedPatientsFeatureOn

  const prescriberIdRef = useRef(prescriber?.id)
  prescriberIdRef.current = prescriber?.id

  const patientSearch = useMemo(
    () =>
      debounce((queryTerm: string) => {
        if (queryTerm.length < 2) return
        if (!isSharedPatientsFeatureOnRef.current && !prescriberIdRef.current) {
          return
        }

        searchPatientsRef.current({
          searchTerm: queryTerm,
          page: 1,
          limit: 10,
          prescriberId: prescriberIdRef.current,
        })
      }, 500),
    []
  )

  useEffect(() => () => patientSearch.cancel(), [patientSearch])

  const fetchSearchResult = useCallback(() => {
    if (patientSearchQuery.length < 2) return
    if (!isSharedPatientsFeatureOn && !prescriber?.id) {
      return
    }

    patientSearch.cancel()
    searchPatients({
      searchTerm: patientSearchQuery,
      page: 1,
      limit: 10,
      prescriberId: prescriber?.id,
    })
  }, [patientSearchQuery, isSharedPatientsFeatureOn, prescriber?.id, searchPatients, patientSearch])

  const handlePatientSearchChange = useCallback(
    (queryTerm: string) => {
      setPatientSearchQuery(queryTerm)
      patientSearch(queryTerm)
    },
    [patientSearch]
  )

  const onSelectAnotherPatient = () => {
    dispatch(selectPatient(undefined))
    setPatientSearchQuery('')
    setShowFetchPatientError(false)
    hasAttemptedIntegrationConnection.current = false
    hasAttemptedFetch.current = false
    if (clientSSOContextData) {
      clearExternalPatientData()
    }
    if (providerId) {
      const errorKey = `integration_failed_${providerId}`
      localStorage.removeItem(errorKey)
    }
  }

  const handleSelectPatient = (patient: any) => {
    if (onPatientChosen) onPatientChosen(patient)
    else dispatch(selectPatient(patient))
    dispatch(patientsApi.util.resetApiState())
    setPatientSearchQuery('')
  }

  const shouldShowPatientLoadFallback =
    !!patientLoadState?.showError && !!prescriber && !isDeactivatedPrescriber
  const shouldShowPatientSearch =
    forcePatientSearch ||
    shouldShowPatientLoadFallback ||
    (!!isAbleToManagePatient &&
      !showFetchPatientError &&
      (!externalPatientFetchId || !isIntegrationConnected))

  return (
    <VStack alignItems="stretch">
      <Heading as="h4" size="md">
        Patient
      </Heading>
      <Card p="3">
        {syncPatientResult.isLoading || patientLoadState?.isLoading ? (
          <LoadingState />
        ) : selectedPatient.id ? (
          <VStack flexGrow="1" flexBasis="0" alignItems="stretch">
            <SelectedPatientIdentity patient={selectedPatient} />
            {isAbleToManagePatient && (
              <Button
                leftIcon={<Icon as={FiRefreshCw} />}
                colorScheme="blue"
                variant="outline"
                onClick={onSelectAnotherPatient}
                {...analyticsAction('new_script_select_another_patient')}
                _hover={{
                  bg: 'blue.50',
                  borderColor: 'blue.300',
                }}
              >
                Select another patient
              </Button>
            )}
          </VStack>
        ) : patientLoadState?.showError || shouldShowPatientSearch ? (
          <>
            {patientLoadState?.showError && (
              <ErrorState
                error={true}
                onSelectAnother={onSelectAnotherPatient}
                canManagePatient={false}
              />
            )}
            {shouldShowPatientSearch && (
              <PatientSearch
                searchQuery={patientSearchQuery}
                onSearchChange={handlePatientSearchChange}
                onSearch={fetchSearchResult}
                onSelectPatient={handleSelectPatient}
                isDeactivated={isDeactivatedPrescriber}
                prescriber={prescriber as Prescriber}
                onAddPatient={handleSelectPatient}
                results={searchPatientsResult.data?.patients ?? []}
                isSearching={searchPatientsResult.isFetching}
                allowRegistration={allowRegistration}
              />
            )}
          </>
        ) : (
          <ErrorState
            error={!!syncPatientResult.error}
            onSelectAnother={onSelectAnotherPatient}
            canManagePatient={!!isAbleToManagePatient}
            errorMessage={getErrorMessage(syncPatientResult.error)}
          />
        )}
      </Card>
    </VStack>
  )
}

export default PatientWorkflow
